<template>
  <section class="monthly-telemetry-report-wrapper">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="ActivityIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Monthly Telemetry') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Daily operation, hourmeter and charts for the selected period') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center mt-1 mt-lg-0 flex-wrap"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="isRefreshing"
          @click="refreshReport"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isRefreshing }"
          />
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="tooltipBtn"
          @click="exportSummary"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Monthly Summary') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'telemetry-summary', params: { id: myId } }"
        >
          <feather-icon
            icon="InfoIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ $t('Daily Data') }}</span>
        </b-button>
      </div>
    </div>

    <b-card
      no-body
      class="asset-header-card mb-2 shadow-sm"
    >
      <div class="asset-header-content p-1">
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center py-50"
        >
          <b-spinner
            variant="primary"
            small
            class="mr-1"
          />
          <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
        </div>

        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || $t('Model')"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || identifier || myId }}
                  </h5>
                </div>

                <b-badge
                  v-if="assetNickname"
                  variant="light-primary"
                  class="ml-50 font-weight-normal"
                >
                  {{ assetNickname }}
                </b-badge>
              </div>

              <div
                v-if="!assetPlate && (assetChassis || identifier)"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis || identifier, $t('chassis'))"
                  >
                    {{ assetChassis || identifier }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div
            class="d-flex align-items-center flex-wrap"
            style="gap: 6px;"
          >
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
            </div>

            <div
              v-if="color"
              class="meta-chip"
            >
              <feather-icon
                icon="DropletIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('Color') }}:</span>
              <span class="meta-value">{{ color }}</span>
            </div>

            <div
              v-if="assetGroupName"
              class="meta-chip"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <div
              v-if="assetCustomerName"
              class="meta-chip"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetFilters"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="showFilters = false"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="singleDateConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="singleDateConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary"
              />
              <span>
                {{ $t('Vehicle:') }}
                <strong class="text-primary">{{ assetModel || identifier || myId }}</strong>.
                {{ $t('Adjust the date range. The maximum interval is 31 days.') }}
              </span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                @click="applyFilters"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center"
      >
        <feather-icon
          icon="LockIcon"
          size="12"
          class="mr-25"
        />
        {{ assetModel || identifier || myId }}
      </b-badge>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetFilters"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-row
      v-if="isLoadingDaily || hasKpiData"
      class="match-height mb-1"
    >
      <b-col
        v-for="kpi in kpiCards"
        :key="kpi.key"
        cols="6"
        xl="3"
        class="mb-1"
      >
        <b-card
          no-body
          class="kpi-card shadow-sm border-0 h-100 p-1"
        >
          <template v-if="isLoadingDaily">
            <b-skeleton
              width="55%"
              height="12px"
              class="mb-50"
            />
            <b-skeleton
              width="70%"
              height="28px"
              class="mb-50"
            />
            <b-skeleton
              width="45%"
              height="10px"
            />
          </template>
          <template v-else>
            <div class="d-flex justify-content-between align-items-start mb-25">
              <div>
                <span class="kpi-label text-muted font-small-1 font-weight-bolder text-uppercase">
                  {{ kpi.label }}
                </span>
                <h3
                  class="font-weight-bolder mb-0 mt-25"
                  :class="kpi.valueClass"
                >
                  {{ kpi.value }} <small class="kpi-unit">h</small>
                </h3>
              </div>
              <b-avatar
                :variant="kpi.variant"
                size="42px"
                class="shadow-xs flex-shrink-0"
              >
                <feather-icon
                  :icon="kpi.icon"
                  size="20"
                />
              </b-avatar>
            </div>
            <div class="kpi-footer font-small-1 text-muted mt-auto pt-50 border-top">
              {{ kpi.hint }}
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      v-if="isLoadingDaily"
      class="shadow-sm border-0 mb-2 text-center py-3"
    >
      <b-spinner
        variant="primary"
        class="mb-1"
      />
      <div class="text-muted font-small-3">
        {{ $t('Loading data...') }}
      </div>
    </b-card>

    <b-card
      v-else-if="showMsgNoDataFound"
      class="shadow-sm border-0 mb-2 text-center py-2"
    >
      <b-avatar
        variant="light-warning"
        size="64px"
        class="mb-1"
      >
        <feather-icon
          icon="CalendarIcon"
          size="28"
          class="text-warning"
        />
      </b-avatar>
      <h4 class="font-weight-bolder mb-50">
        {{ $t('No telemetry in this period') }}
      </h4>
      <p class="text-muted font-small-3 mb-0 mx-auto empty-copy">
        {{ $t('Adjust the date range. The maximum interval is 31 days.') }}
      </p>
    </b-card>

    <template v-if="!isLoadingDaily && !showMsgNoDataFound">
      <b-card
        no-body
        class="section-card shadow-sm border-0 mb-2"
      >
        <div class="section-card-header">
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-primary"
              size="38px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                icon="CalendarIcon"
                size="18"
                class="text-primary"
              />
            </b-avatar>
            <div>
              <h5 class="mb-0 font-weight-bolder">
                {{ $t('Operational calendar') }}
              </h5>
              <small class="text-muted">{{ $t('Hours and distance recorded each day') }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap legend-row">
            <span class="legend-pill">
              <span class="wc-tag wc-tag-hours">H</span>
              {{ $t('Working Hours') }}
            </span>
            <span class="legend-pill">
              <span class="wc-tag wc-tag-km">Km</span>
              {{ $t('Distance') }}
            </span>
          </div>
        </div>
        <div class="app-calendar overflow-hidden">
          <full-calendar
            :key="calendarKey"
            ref="refCalendar"
            :options="calendarOptions"
            class="full-calendar"
          />
        </div>
      </b-card>

      <b-card
        v-if="visibleChartsCount > 0"
        no-body
        class="section-card shadow-sm border-0 mb-1"
      >
        <div class="section-card-header">
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-info"
              size="38px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                icon="BarChart2Icon"
                size="18"
                class="text-info"
              />
            </b-avatar>
            <div>
              <h5 class="mb-0 font-weight-bolder">
                {{ $t('Charts') }}
              </h5>
              <small class="text-muted">
                {{ expandedChartsCount }} {{ $t('of') }} {{ visibleChartsCount }} {{ $t('charts expanded') }}
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-50 d-inline-flex align-items-center"
              @click="expandAllCharts"
            >
              <feather-icon
                icon="ChevronsDownIcon"
                size="14"
                class="mr-50"
              />
              {{ $t('Expand All') }}
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="d-inline-flex align-items-center"
              @click="collapseAllCharts"
            >
              <feather-icon
                icon="ChevronsUpIcon"
                size="14"
                class="mr-50"
              />
              {{ $t('Collapse All') }}
            </b-button>
          </div>
        </div>
      </b-card>

      <template v-for="chart in chartsList">
      <b-card
        v-if="shouldShowChart(chart)"
        :key="chart.id"
        no-body
        class="chart-card shadow-sm border-0 mb-1"
      >
        <button
          type="button"
          class="chart-card-header"
          @click="toggleChart(chart.id)"
        >
          <div class="d-flex align-items-center min-width-0">
            <b-avatar
              variant="light-primary"
              size="32px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                :icon="chartIcon(chart.id)"
                size="16"
                class="text-primary"
              />
            </b-avatar>
            <h5 class="mb-0 chart-title text-truncate">
              {{ chart.title }}
            </h5>
          </div>
          <div class="d-flex align-items-center flex-shrink-0">
            <b-badge
              v-if="chart.unit"
              variant="light-secondary"
              class="chart-unit mr-50"
            >
              {{ chart.unit }}
            </b-badge>
            <feather-icon
              :icon="chart.expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted"
            />
          </div>
        </button>
        <b-collapse :visible="chart.expanded">
          <div class="px-1 pb-1">
            <monthly-telemetry-chart
              :title="chart.title"
              :axis-x="chart.axisX"
              :axis-y="chart.axisY"
              :unit="chart.unit"
            />
          </div>
        </b-collapse>
      </b-card>
      </template>

      <b-card
        v-if="hasStatusData"
        no-body
        class="chart-card shadow-sm border-0 mb-1"
      >
        <button
          type="button"
          class="chart-card-header"
          @click="showStatusChart = !showStatusChart"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-warning"
              size="32px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                icon="PieChartIcon"
                size="16"
                class="text-warning"
              />
            </b-avatar>
            <h5 class="mb-0 chart-title">
              {{ $t('Status Chart') }}
            </h5>
          </div>
          <feather-icon
            :icon="showStatusChart ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="18"
            class="text-muted"
          />
        </button>
        <b-collapse :visible="showStatusChart">
          <div class="px-1 pb-1">
            <chart-status
              :labels="statusLabels"
              :values="statusValue"
            />
          </div>
        </b-collapse>
      </b-card>

      <b-card
        v-if="latlngs.length > 0 && !isLoading"
        no-body
        class="chart-card heat-map-card shadow-sm border-0 mb-2"
        :class="{ 'is-fullscreen': isHeatMapFullscreen }"
      >
        <div class="chart-card-header">
          <button
            type="button"
            class="heat-map-title-btn"
            @click="showHeatMap = !showHeatMap"
          >
            <b-avatar
              variant="light-danger"
              size="32px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                icon="MapPinIcon"
                size="16"
                class="text-danger"
              />
            </b-avatar>
            <div class="text-left min-width-0">
              <h5 class="mb-0 chart-title text-truncate">
                {{ $t('Heat Map') }}
              </h5>
              <small class="text-muted d-none d-sm-block">{{ $t('Where the asset operated') }}</small>
            </div>
            <feather-icon
              :icon="showHeatMap ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted ml-75 flex-shrink-0"
            />
          </button>
          <b-button
            v-b-tooltip.hover
            :variant="isHeatMapFullscreen ? 'primary' : 'outline-primary'"
            size="sm"
            class="d-inline-flex align-items-center flex-shrink-0"
            :title="isHeatMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
            @click="toggleHeatMapFullscreen"
          >
            <feather-icon
              :icon="isHeatMapFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'"
              size="14"
              class="mr-50"
            />
            <span class="d-none d-sm-inline">
              {{ isHeatMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen') }}
            </span>
          </b-button>
        </div>
        <b-collapse :visible="showHeatMap">
          <div class="heat-map-stage px-1 pb-1">
            <div class="heat-map-toolbar">
              <div
                class="d-flex align-items-center flex-wrap"
                style="gap: 8px;"
              >
                <span class="font-small-2 font-weight-bolder text-muted">{{ $t('Display:') }}</span>
                <b-button-group
                  size="sm"
                  class="map-layer-switch"
                >
                  <b-button
                    :variant="btnMap ? 'primary' : 'outline-primary'"
                    class="d-inline-flex align-items-center"
                    @click="chooseLayer('map')"
                  >
                    <feather-icon
                      icon="MapIcon"
                      size="13"
                      class="mr-25"
                    />
                    <span>{{ $t('Map') }}</span>
                  </b-button>
                  <b-button
                    :variant="btnGround ? 'primary' : 'outline-primary'"
                    class="d-inline-flex align-items-center"
                    @click="chooseLayer('ground')"
                  >
                    <feather-icon
                      icon="ImageIcon"
                      size="13"
                      class="mr-25"
                    />
                    <span>{{ $t('Satellite') }}</span>
                  </b-button>
                </b-button-group>
              </div>
              <div
                class="d-flex align-items-center flex-wrap heat-map-meta"
                style="gap: 8px;"
              >
                <b-badge
                  variant="light-danger"
                  pill
                >
                  {{ latlngs.length }}
                </b-badge>
                <small class="text-muted font-weight-bold">{{ $t('Recorded points') }}</small>
                <b-button
                  v-b-tooltip.hover
                  variant="outline-primary"
                  size="sm"
                  class="d-inline-flex align-items-center"
                  :title="$t('Adjust Zoom')"
                  @click="fitHeatMap"
                >
                  <feather-icon
                    icon="CrosshairIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span class="d-none d-md-inline">{{ $t('Adjust Zoom') }}</span>
                </b-button>
              </div>
            </div>
            <div class="heat-map">
              <l-map
                ref="myMap"
                class="heat-map-canvas"
                :zoom="heatMapZoom"
                :center="center"
                :options="heatMapOptions"
                :key="heatMapRenderKey"
                @ready="onHeatMapReady"
              >
                <l-tile-layer :url="url" />
                <Vue2LeafletHeatmap
                  v-if="latlngs.length > 0"
                  :lat-lng="latlngs"
                  :radius="25"
                  :blur="18"
                  :max-zoom="17"
                  :min-opacity="0.3"
                  :gradient="heatMapGradient"
                />
              </l-map>
              <div class="heat-legend">
                <span>{{ $t('Low') }}</span>
                <span class="heat-legend-bar" />
                <span>{{ $t('High') }}</span>
              </div>
            </div>
          </div>
        </b-collapse>
      </b-card>

      <div
        v-show="isLoading"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          small
          class="mr-50"
        />
        <span class="text-muted font-small-3">{{ $t('Loading') }}</span>
      </div>

      <b-card
        v-if="hasTableData"
        no-body
        class="chart-card shadow-sm border-0 mb-2"
      >
        <div class="chart-card-header table-toolbar">
          <button
            type="button"
            class="d-flex align-items-center border-0 bg-transparent p-0 text-left"
            @click="showTable = !showTable"
          >
            <b-avatar
              variant="light-success"
              size="32px"
              class="mr-75 flex-shrink-0"
            >
              <feather-icon
                icon="ListIcon"
                size="16"
                class="text-success"
              />
            </b-avatar>
            <div>
              <h5 class="mb-0 chart-title">
                {{ $t('Daily Telemetry Data') }}
              </h5>
              <small class="text-muted">{{ $t('Daily readings') }}</small>
            </div>
            <feather-icon
              :icon="showTable ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted ml-75"
            />
          </button>

          <div class="d-flex align-items-center flex-wrap table-actions">
            <b-dropdown
              variant="outline-secondary"
              size="sm"
              boundary="viewport"
              menu-class="graphic-telemetry-column-menu"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="ColumnsIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('Columns') }}
              </template>

              <div
                class="px-1 py-1"
                @click.stop
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-muted font-weight-bold">{{ $t('Select Columns') }}</small>
                  <div>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-primary"
                      @click="selectAllTableColumns"
                    >
                      {{ $t('All') }}
                    </b-button>
                    <span class="mx-50 text-muted">|</span>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-primary"
                      @click="deselectAllTableColumns"
                    >
                      {{ $t('None') }}
                    </b-button>
                  </div>
                </div>

                <div class="graphic-telemetry-column-options">
                  <b-form-checkbox-group
                    v-model="selectedTableColumns"
                    stacked
                  >
                    <b-form-checkbox
                      v-for="column in availableTableColumns"
                      :key="column.key"
                      :value="column.key"
                      class="mb-50"
                    >
                      <span class="column-option-text">{{ column.label }}</span>
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </div>

                <div class="text-center border-top pt-50 mt-50">
                  <small class="text-muted">
                    {{ selectedTableColumns.length }} {{ $t('of') }} {{ availableTableColumns.length }} {{ $t('columns selected') }}
                  </small>
                </div>
              </div>
            </b-dropdown>

            <b-button
              variant="outline-success"
              size="sm"
              class="d-inline-flex align-items-center"
              :disabled="isExportingTable"
              @click="exportTableToExcel"
            >
              <b-spinner
                v-if="isExportingTable"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="DownloadIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('Export Sheet') }}</span>
            </b-button>
          </div>
        </div>

        <b-collapse :visible="showTable">
          <div class="px-1 pb-1">
            <div class="table-container">
              <b-table
                :items="tableData"
                :busy="isLoadingDaily"
                responsive
                :fields="visibleTableFields"
                primary-key="date"
                show-empty
                :empty-text="$t('No data available')"
                tbody-tr-class="excel-row"
                thead-tr-class="excel-header"
                table-class="excel-table"
                hover
                small
                class="position-relative mb-0"
              >
                <template #table-busy>
                  <div class="text-center my-2">
                    <b-spinner
                      variant="primary"
                      class="mb-50"
                    />
                    <div class="text-muted font-small-3">
                      {{ $t('Loading data...') }}
                    </div>
                  </div>
                </template>

                <template
                  v-for="field in visibleTableFields"
                  #[`cell(${field.key})`]="data"
                >
                  <div
                    :key="field.key"
                    class="excel-cell"
                    :class="getCellClass(field.key, data.value)"
                  >
                    {{ formatTableCellValue(field.key, data.value, data.item) }}
                  </div>
                </template>
              </b-table>
            </div>

            <div
              v-if="tableData.length > 0"
              class="d-flex justify-content-between align-items-center mt-1"
            >
              <small class="text-muted">
                {{ $t('Showing') }} {{ tableData.length }} {{ $t('entries') }}
              </small>
            </div>
          </div>
        </b-collapse>
      </b-card>
    </template>

    <b-modal
      ref="modal-export"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
    >
      <div class="text-center py-1">
        <b-avatar
          variant="light-primary"
          size="56px"
          class="mb-1"
        >
          <feather-icon
            icon="DownloadIcon"
            size="24"
            class="text-primary"
          />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">
          {{ $t('Preparing report') }}
        </h5>
        <p class="text-muted font-small-3 mb-1">
          {{ $t('Loading') }}...
        </p>
        <b-spinner variant="primary" />
      </div>
    </b-modal>
  </section>
</template>

<script>
import WorkHours from '../telemetry/components/ChartDailyWorkHours.vue'
import ChartStatus from './components/ChartStatus.vue'
import ChartPercentages from './components/ChartPercentages.vue'
import ChartConsumption from './components/ChartConsumption.vue'
import MonthlyTelemetryChart from './components/MonthlyTelemetryChart.vue'

import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  getCurrentInstance,
  onUnmounted
} from '@vue/composition-api'
import store from '@/store'
import router from '@/router'

import queriesStoreModule from '../queriesStoreModule'

import {
  BTabs,
  BTab,
  BCardText,
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BSpinner,
  BButtonGroup,
  BButton,
  BFormCheckbox,
  BFormCheckboxGroup,
  BTable,
  BCollapse,
  BBadge,
  BInputGroup,
  BInputGroupPrepend,
  BAvatar,
  BSkeleton,
  BDropdown,
  BLink
} from 'bootstrap-vue'
import ChartFuel from './components/ChartFuel.vue'

import {
  LMap,
  LTileLayer,
} from "vue2-leaflet";

import 'leaflet/dist/leaflet.css'
import Vue2LeafletHeatmap from "@core/components/heat-map/Vue2LeafletHeatmap";

import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'

import interactionPlugin from '@fullcalendar/interaction'
import '@fullcalendar/core/locales/pt-br'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'

export default {

  components: {
    MonthlyTelemetryChart,
    WorkHours,
    BTabs,
    BTab,
    BCardText,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BSpinner,
    BFormCheckbox,
    BFormCheckboxGroup,
    BTable,
    BCollapse,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BAvatar,
    BSkeleton,
    BDropdown,
    BLink,
    ChartStatus,
    ChartPercentages,
    ChartConsumption,
    ChartFuel,
    BButtonGroup,
    BButton,

    LMap,
    LTileLayer,
    Vue2LeafletHeatmap,
    FullCalendar,
    flatPickr
  },

  setup() {
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t ? instance.proxy.$t.bind(instance.proxy) : (v) => v

    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'

    const myId = router.currentRoute.params.id

    const deviceId = ref('')
    const calendarKey = ref(1)
    const tooltipBtn = ref($t('Download Summary'))
    const showFilters = ref(false)
    const dateStart = ref('')
    const dateEnd = ref('')
    const dateRangePreset = ref('previousMonth')
    const dtS = ref('')
    const dtE = ref('')

    const singleDateConfig = {
      maxDate: new Date(),
      minDate: new Date(new Date().setMonth(new Date().getMonth() - 4)),
      dateFormat: 'd/m/Y',
      allowInput: true,
    }

    const isLoading = ref(true)
    const isLoadingDaily = ref(true)
    const showMsgNoDataFound = ref(false)
    const showCalendar = ref(false)

    // Heatmap
    const center = ref([0, 0])
    const latlngs = ref([])
    const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
    const btnGround = ref(false)
    const btnMap = ref(true)
    const showHeatMap = ref(true)
    const isHeatMapFullscreen = ref(false)
    const heatMapZoom = ref(6)
    const heatMapRenderKey = ref(0)
    const heatMapOptions = {
      scrollWheelZoom: false,
      zoomControl: true,
    }
    const heatMapGradient = {
      0.2: '#3b82f6',
      0.4: '#22d3ee',
      0.6: '#22c55e',
      0.8: '#facc15',
      1.0: '#ef4444',
    }

    // Charts extras
    const showStatusChart = ref(true)

    const chartsExpanded = ref({})

    // KPIs
    const startHourmeter = ref(0)
    const endHourmeter = ref(0)
    const workHours = ref(0)
    const averageDayWorkedHours = ref(0)

    const tableData = ref([])
    const selectedTableColumns = ref([])
    const isExportingTable = ref(false)
    const showTable = ref(true)

    const calendarOptions = ref({
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      weekends: true,
      editable: false,
      droppable: false,
      eventResizableFromStart: true,
      headerToolbar: { left: '', center: 'title', right: '' },
      events: [],
      locale: 'pt-br',
      timeZone: 'UTC',
    })

    const identifier = ref('')
    const imgUrl = ref('')
    const modelInfo = ref('')
    const color = ref('')
    const loadingAsset = ref(true)
    const assetData = ref(null)
    const fallbackAvatar = require('@/assets/images/assets/Car.png')

    const axisX = ref([])
    const axisY = ref([])

    const consumptionAxisX = ref([])
    const consumptionAxisY = ref([])

    const fuelAxisX = ref([])
    const fuelAxisY = ref([])

    const batteryVoltageAxisX = ref([])
    const batteryVoltageAxisY = ref([])

    const dayErrorsAxisX = ref([])
    const dayErrorsAxisY = ref([])

    const fuelPressureAxisX = ref([])
    const fuelPressureAxisY = ref([])

    const engineLoadAxisX = ref([])
    const engineLoadAxisY = ref([])

    const engineOilPressureAxisX = ref([])
    const engineOilPressureAxisY = ref([])

    const engineCoolantTempAxisX = ref([])
    const engineCoolantTempAxisY = ref([])

    const hydraulicPressureAxisX = ref([])
    const hydraulicPressureAxisY = ref([])

    const hydraulicOilTempAxisX = ref([])
    const hydraulicOilTempAxisY = ref([])

    const powerBatteryChargeAxisX = ref([])
    const powerBatteryChargeAxisY = ref([])

    const powerBatteryLifeAxisX = ref([])
    const powerBatteryLifeAxisY = ref([])

    const powerBatteryTemperatureAxisX = ref([])
    const powerBatteryTemperatureAxisY = ref([])

    const powerBatteryVoltsAxisX = ref([])
    const powerBatteryVoltsAxisY = ref([])

    const speedAxisX = ref([])
    const speedAxisY = ref([])

    const transmissionTempAxisX = ref([])
    const transmissionTempAxisY = ref([])

    const turboPressureAxisX = ref([])
    const turboPressureAxisY = ref([])

    const statusLabels = ref([])
    const statusValue = ref([])

    const metricsPercentages = ref([])
    const metricsLabels = ref([])

    const formatKpiValue = (value) => {
      const n = Number(value)
      if (!Number.isFinite(n) || n === 0) return '0.00'
      return n.toFixed(2)
    }

    const formatAverageKpiValue = (value) => {
      const n = Number(value)
      if (!Number.isFinite(n) || n === 0) return '0'
      // no máximo 2 casas decimais (sem forçar trailing zeros)
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n)
    }

    const hasKpiData = computed(() => {
      const values = [startHourmeter.value, endHourmeter.value, workHours.value, averageDayWorkedHours.value]
      return values.some(v => Number(v) > 0)
    })

    const kpiCards = computed(() => ([
      {
        key: 'start',
        label: $t('Start Hourmeter'),
        value: formatKpiValue(startHourmeter.value),
        hint: $t('First reading in the period'),
        icon: 'PlayCircleIcon',
        variant: 'light-primary',
        valueClass: 'text-primary',
      },
      {
        key: 'end',
        label: $t('End Hourmeter'),
        value: formatKpiValue(endHourmeter.value),
        hint: $t('Last reading in the period'),
        icon: 'StopCircleIcon',
        variant: 'light-success',
        valueClass: 'text-success',
      },
      {
        key: 'work',
        label: $t('Total Work Hours'),
        value: formatKpiValue(workHours.value),
        hint: $t('Sum of worked hours'),
        icon: 'ClockIcon',
        variant: 'light-warning',
        valueClass: 'text-warning',
      },
      {
        key: 'average',
        label: $t('Average Daily Hours'),
        value: formatAverageKpiValue(averageDayWorkedHours.value),
        hint: $t('Average per day with operation'),
        icon: 'DivideCircleIcon',
        variant: 'light-info',
        valueClass: 'text-info',
      },
    ]))

    const isRefreshing = computed(() => isLoadingDaily.value || isLoading.value)

    const assetPlate = computed(() => assetData.value?.plate || '')
    const assetChassis = computed(() => assetData.value?.chassisNumber || '')
    const assetManufacturer = computed(() => (
      assetData.value?.manufacturerName
      || assetData.value?.brandName
      || assetData.value?.manufacturer
      || ''
    ))
    const assetModel = computed(() => {
      if (!assetData.value) return modelInfo.value
      const name = assetData.value.modelIdName || assetData.value.modelName || ''
      const year = assetData.value.modelYear ? `(${assetData.value.modelYear})` : ''
      return `${name} ${year}`.trim() || modelInfo.value
    })
    const assetNickname = computed(() => assetData.value?.nickname || '')
    const assetGroupName = computed(() => assetData.value?.assetGroupName || assetData.value?.groupName || '')
    const assetCustomerName = computed(() => assetData.value?.customerName || assetData.value?.ownerName || '')
    const assetAvatar = computed(() => (typeof imgUrl.value === 'string' && imgUrl.value ? imgUrl.value : fallbackAvatar))

    const formatPeriodDate = (iso) => {
      if (!iso) return ''
      const date = new Date(iso)
      if (Number.isNaN(date.getTime())) return ''
      const day = String(date.getUTCDate()).padStart(2, '0')
      const month = String(date.getUTCMonth() + 1).padStart(2, '0')
      const year = date.getUTCFullYear()
      return `${day}/${month}/${year}`
    }

    const periodLabel = computed(() => {
      if (!dtS.value || !dtE.value) return ''
      return `${formatPeriodDate(dtS.value)} — ${formatPeriodDate(dtE.value)}`
    })

    const activeFiltersCount = computed(() => (dtS.value && dtE.value ? 1 : 0))

    const periodPresets = computed(() => ([
      { id: 'today', label: $t('Today') },
      { id: 'yesterday', label: $t('Yesterday') },
      { id: 'last7', label: $t('Last 7 days') },
      { id: 'month', label: $t('This Month') },
      { id: 'previousMonth', label: $t('Previous Month') },
    ]))

    const goBack = () => {
      router.back()
    }

    const handleImageError = (event) => {
      if (event?.target) event.target.src = fallbackAvatar
    }

    const getCalendarApi = () => {
      const cal = instance?.proxy?.$refs?.refCalendar
      return cal && cal.getApi ? cal.getApi() : null
    }

    const updateCalendar = () => {
      const api = getCalendarApi()
      if (!api) return
      if (!dtS.value) return
      const start = new Date(dtS.value)
      const newDate = new Date(start.getUTCFullYear(), start.getUTCMonth(), 1)
      nextTick(() => {
        api.gotoDate(newDate)
        api.render()
      })
    }

    const renderWorkSummaryEventContent = (arg) => {
      const hours = Number(arg.event.extendedProps?.hours ?? 0)
      const km = Number(arg.event.extendedProps?.km ?? 0)
      const hasHours = Number.isFinite(hours) && hours > 0
      const hasKm = Number.isFinite(km) && km > 0

      const lines = []
      if (hasHours) lines.push(`<div class="wc-line"><span class="wc-tag wc-tag-hours">H</span><span class="wc-value">${hours.toFixed(2)}</span></div>`)
      if (hasKm) lines.push(`<div class="wc-line"><span class="wc-tag wc-tag-km">Km</span><span class="wc-value">${km.toFixed(0)}</span></div>`)
      const title = [
        hasHours ? `${$t('Working Hours')}: ${hours.toFixed(2)} h` : null,
        hasKm ? `${$t('Distance')}: ${km.toFixed(1)} km` : null,
      ].filter(Boolean).join(' | ')
      return { html: `<div class="work-cal-event" title="${title}"><span class="wc-dot"></span><div class="wc-body">${lines.join('')}</div></div>` }
    }

    calendarOptions.value.eventContent = renderWorkSummaryEventContent

    const hasAnyPositiveDailyData = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return false
      const numericFields = [
        'hours',
        'totalOdometerDistance',
        'fuelLevel',
        'meanFuelConsumption',
        'engineCoolantTemp',
        'hydraulicOilTemp',
        'transmissionTemp',
        'dayErrors',
        'fuelPressure',
        'engineOilPressure',
        'turboPressure',
        'batteryVoltage',
        'acceleration',
        'speed',
        'engineLoad',
        'rpm',
        'dayErrorsCount',
        'hydraulicPressure',
        'powerBatteryCharge',
        'powerBatteryLife',
        'powerBatteryVolts',
        'powerBatteryTemperature',
      ]
      return rows.some(row => numericFields.some(f => Number(row?.[f] ?? 0) > 0))
    }

    const formatDisplayDate = (date) => (
      `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
    )

    const rangeForPreset = (preset) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      let start = new Date(today)
      let end = new Date(today)
      if (preset === 'yesterday') {
        start.setDate(start.getDate() - 1)
        end = new Date(start)
      } else if (preset === 'last7') {
        start.setDate(start.getDate() - 6)
      } else if (preset === 'month') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
      } else if (preset === 'previousMonth') {
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        end = new Date(today.getFullYear(), today.getMonth(), 0)
      }
      return { start, end }
    }

    const startDates = () => {
      const { start, end } = rangeForPreset('previousMonth')
      dateRangePreset.value = 'previousMonth'
      dateStart.value = formatDisplayDate(start)
      dateEnd.value = formatDisplayDate(end)
      dtS.value = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0)).toISOString()
      dtE.value = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)).toISOString()
      calendarOptions.value.initialDate = dtS.value
    }

    const showSnackbar = (config = { title: '', icon: '', variant: 'info' }) => {
      if (!instance?.proxy?.$toast) return
      instance.proxy.$toast({
        component: ToastificationContent,
        props: {
          title: config.title,
          icon: config.icon,
          variant: config.variant,
        },
      })
    }

    const parseDisplayDate = (value) => {
      const match = String(value || '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (!match) return null
      const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
      if (Number.isNaN(date.getTime()) || date.getDate() !== Number(match[1])) return null
      return date
    }

    const syncPresetFromFields = () => {
      const matched = ['today', 'yesterday', 'last7', 'month', 'previousMonth'].find((preset) => {
        const range = rangeForPreset(preset)
        return formatDisplayDate(range.start) === dateStart.value && formatDisplayDate(range.end) === dateEnd.value
      })
      dateRangePreset.value = matched || 'custom'
    }

    const commitDateRange = (start, end) => {
      if (!start || !end || end < start) {
        showSnackbar({ title: $t('Invalid Range'), icon: 'AlertTriangleIcon', variant: 'warning' })
        return false
      }
      const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24))
      const today = new Date()
      today.setHours(23, 59, 59, 999)
      if (diffDays > 31 || end > today) {
        showSnackbar({ title: $t('Invalid Range'), icon: 'AlertTriangleIcon', variant: 'warning' })
        return false
      }
      dtS.value = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0)).toISOString()
      dtE.value = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)).toISOString()
      calendarOptions.value.initialDate = dtS.value
      calendarKey.value += 1
      nextTick(updateCalendar)
      fetchDailyWorkHours()
      return true
    }

    const applyFilters = () => {
      commitDateRange(parseDisplayDate(dateStart.value), parseDisplayDate(dateEnd.value))
    }

    const onCustomDateChange = () => {
      syncPresetFromFields()
    }

    const setDatePreset = (preset) => {
      const range = rangeForPreset(preset)
      dateRangePreset.value = preset
      dateStart.value = formatDisplayDate(range.start)
      dateEnd.value = formatDisplayDate(range.end)
    }

    const resetFilters = () => {
      setDatePreset('previousMonth')
      applyFilters()
    }

    const copyToClipboard = (text, label) => {
      if (!text) return
      const successTitle = `${label || $t('text')} ${$t('copiedToClipboard')}!`
      const failTitle = $t('failedToCopyToClipboard')
      const notifySuccess = () => showSnackbar({ title: successTitle, icon: 'CheckIcon', variant: 'success' })
      const notifyError = () => showSnackbar({ title: failTitle, icon: 'AlertTriangleIcon', variant: 'danger' })
      const proxy = instance?.proxy
      if (proxy?.$copyText) {
        proxy.$copyText(text).then(notifySuccess).catch(notifyError)
        return
      }
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError)
      }
    }

    const chooseLayer = (layer) => {
      const selected = layer === 'ground' ? 'ground' : 'map'
      localStorage.layerHeatMap = selected
      if (selected === 'ground') {
        btnGround.value = true
        btnMap.value = false
        url.value = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        return
      }
      btnGround.value = false
      btnMap.value = true
      url.value = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    }

    const fetchPlaces = async () => {
      isLoading.value = true
      latlngs.value = []
      try {
        const resp = await store.dispatch('app-queries/fetchPlaces', {
          id: myId,
          dtS: dtS.value,
          dtE: dtE.value,
        })
        const places = resp?.data?.places
        if (!Array.isArray(places) || places.length === 0) return

        const validPlaces = places.filter(p => p.lat && p.lat !== 0 && p.lng && p.lng !== 0)
        if (validPlaces.length === 0) return

        latlngs.value = validPlaces.map(p => [parseFloat(p.lat), parseFloat(p.lng), 1])
        const sumLat = validPlaces.reduce((sum, place) => sum + Number(place.lat), 0)
        const sumLng = validPlaces.reduce((sum, place) => sum + Number(place.lng), 0)
        center.value = [sumLat / validPlaces.length, sumLng / validPlaces.length]
        heatMapRenderKey.value += 1
      } catch (_) {} finally {
        isLoading.value = false
      }
    }

    const exportSummary = async () => {
      try {
        const modal = instance?.proxy?.$refs?.['modal-export']
        if (modal?.show) modal.show()

        const response = await store.dispatch('app-queries/exportMonthlySummaryToExcel', {
          assetId: myId,
          start: dtS.value,
          end: dtE.value,
        })

        const date = new Date(dtS.value)
        const mm = date.getUTCMonth() + 1
        const urlBlob = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = urlBlob
        link.setAttribute('download', `monthly_summary_${mm}.xls`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(urlBlob)

        showSnackbar({ title: $t('Export successful'), icon: 'CheckIcon', variant: 'success' })
        if (modal?.hide) modal.hide()
      } catch (e) {
        showSnackbar({ title: $t('Error exporting report'), icon: 'AlertTriangleIcon', variant: 'danger' })
        const modal = instance?.proxy?.$refs?.['modal-export']
        if (modal?.hide) modal.hide()
      }
    }

    // Resolve icon (função pura, sem dependências de "this")
    const resolveIcon = (assetType) => {
      if (assetType === 1) return require('@/assets/images/assets/Car.png')
      if (assetType === 2) return require('@/assets/images/assets/Motorcycle.png')
      if (assetType === 3) return require('@/assets/images/assets/Truck.png')
      if (assetType === 4) return require('@/assets/images/assets/TruckTractor.png')
      if (assetType === 5) return require('@/assets/images/assets/Van.png')
      if (assetType === 6) return require('@/assets/images/assets/Bus.png')
      if (assetType === 7) return require('@/assets/images/assets/Pickup.png')
      if (assetType === 8) return require('@/assets/images/assets/Excavator.png')
      if (assetType === 9) return require('@/assets/images/assets/Grader.png')
      if (assetType === 10) return require('@/assets/images/assets/WheelLoader.png')
      if (assetType === 11) return require('@/assets/images/assets/Dozer.png')
      if (assetType === 12) return require('@/assets/images/assets/BackhoeLoader.png')
      if (assetType === 13) return require('@/assets/images/assets/SkidSteerLoader.png')
      if (assetType === 14) return require('@/assets/images/assets/Boat.png')
      if (assetType === 15) return require('@/assets/images/assets/JetSki.png')
      if (assetType === 16) return require('@/assets/images/assets/BoxTruck.png')
      if (assetType === 17) return require('@/assets/images/assets/MixerTruck.png')
      if (assetType === 18) return require('@/assets/images/assets/WaterTruck.png')
      if (assetType === 19) return require('@/assets/images/assets/CraneTruck.png')
      if (assetType === 20) return require('@/assets/images/assets/RoadRoller.png')
      if (assetType === 21) return require('@/assets/images/assets/ForkLift.png')
      if (assetType === 22) return require('@/assets/images/assets/ScissorLift.png')
      if (assetType === 23) return require('@/assets/images/assets/ForkLiftCabin.png')
      if (assetType === 24) return require('@/assets/images/assets/ContainerLifter.png')
      if (assetType === 25) return require('@/assets/images/assets/ArticulatedLift.png')
      if (assetType === 26) return require('@/assets/images/assets/CombineHarvester.png')
      if (assetType === 27) return require('@/assets/images/assets/SealcoatTruck.png')
      if (assetType === 28) return require('@/assets/images/assets/IrrigationPivot.png')
      if (assetType === 29) return require('@/assets/images/assets/SecurityHelmet.png')
      if (assetType === 30) return require('@/assets/images/assets/CropSprayer.png')
      return require('@/assets/images/assets/Car.png')
    }

    const fetchAsset = async () => {
      loadingAsset.value = true
      try {
        const response = await store.dispatch('app-queries/fetchAsset', { id: myId })
        const data = response?.data || null
        assetData.value = data
        deviceId.value = data?.devices?.[0]?.id || ''
        identifier.value = data?.plate ? data.plate : data?.chassisNumber
        imgUrl.value = resolveIcon(data?.assetType)
        modelInfo.value = `${data?.modelIdName ? data.modelIdName : data?.modelName || ''} ${data?.modelYear || ''}`.trim()
        color.value = data?.color || ''
      } catch (e) {
        // manter silencioso, a UI ainda funciona sem esses dados
      } finally {
        loadingAsset.value = false
      }
    }

    const fetchDailyWorkHours = async () => {
      isLoadingDaily.value = true
      tableData.value = []
      axisX.value = []
      axisY.value = []
      consumptionAxisX.value = []
      consumptionAxisY.value = []
      fuelAxisX.value = []
      fuelAxisY.value = []
      batteryVoltageAxisX.value = []
      batteryVoltageAxisY.value = []
      dayErrorsAxisX.value = []
      dayErrorsAxisY.value = []
      fuelPressureAxisX.value = []
      fuelPressureAxisY.value = []
      engineLoadAxisX.value = []
      engineLoadAxisY.value = []
      engineOilPressureAxisX.value = []
      engineOilPressureAxisY.value = []
      engineCoolantTempAxisX.value = []
      engineCoolantTempAxisY.value = []
      hydraulicPressureAxisX.value = []
      hydraulicPressureAxisY.value = []
      hydraulicOilTempAxisX.value = []
      hydraulicOilTempAxisY.value = []
      powerBatteryChargeAxisX.value = []
      powerBatteryChargeAxisY.value = []
      powerBatteryLifeAxisX.value = []
      powerBatteryLifeAxisY.value = []
      powerBatteryTemperatureAxisX.value = []
      powerBatteryTemperatureAxisY.value = []
      powerBatteryVoltsAxisX.value = []
      powerBatteryVoltsAxisY.value = []
      speedAxisX.value = []
      speedAxisY.value = []
      transmissionTempAxisX.value = []
      transmissionTempAxisY.value = []
      turboPressureAxisX.value = []
      turboPressureAxisY.value = []

      try {
        const res = await store.dispatch('app-queries/fetchDailyWorkSummary', {
          id: myId,
          dtS: dtS.value,
          dtE: dtE.value,
        })

        const payload = res?.data || {}
        startHourmeter.value = payload.startHourmeter || 0
        endHourmeter.value = payload.endHourmeter || 0
        workHours.value = payload.workHours || 0
        averageDayWorkedHours.value = payload.averageDayWorkedHours || 0

        statusLabels.value = [$t('Proper'), $t('Iddle'), $t('Severe')]
        statusValue.value = [
          Number(payload.normalUsePercentage?.toFixed?.(2) ? payload.normalUsePercentage.toFixed(2) : payload.normalUsePercentage) || 0,
          Number(payload.iddleUsePercentage?.toFixed?.(2) ? payload.iddleUsePercentage.toFixed(2) : payload.iddleUsePercentage) || 0,
          Number(payload.severeUsePercentage?.toFixed?.(2) ? payload.severeUsePercentage.toFixed(2) : payload.severeUsePercentage) || 0,
        ]

        const rows = Array.isArray(payload.data) ? payload.data : []
        tableData.value = rows.map(node => ({ ...node, id: node.date }))

        const arrEvents = []
        rows.forEach(node => {
          const [datePart] = String(node.date || '').split('T')
          if (!datePart) return

          const [yyyy, mm, dd] = datePart.split('-')
          const formattedDate = `${dd}/${mm}`
          axisX.value.push(formattedDate)
          axisY.value.push(node.hours)

          // Popular séries para os gráficos mensais
          consumptionAxisX.value.push(formattedDate)
          consumptionAxisY.value.push(node.meanFuelConsumption)
          fuelAxisX.value.push(formattedDate)
          fuelAxisY.value.push(node.fuelLevel)
          batteryVoltageAxisX.value.push(formattedDate)
          batteryVoltageAxisY.value.push(node.batteryVoltage)
          dayErrorsAxisX.value.push(formattedDate)
          dayErrorsAxisY.value.push(node.dayErrors)
          fuelPressureAxisX.value.push(formattedDate)
          fuelPressureAxisY.value.push(node.fuelPressure)
          engineLoadAxisX.value.push(formattedDate)
          engineLoadAxisY.value.push(node.engineLoad)
          engineOilPressureAxisX.value.push(formattedDate)
          engineOilPressureAxisY.value.push(node.engineOilPressure)
          engineCoolantTempAxisX.value.push(formattedDate)
          engineCoolantTempAxisY.value.push(node.engineCoolantTemp)
          hydraulicPressureAxisX.value.push(formattedDate)
          hydraulicPressureAxisY.value.push(node.hydraulicPressure)
          hydraulicOilTempAxisX.value.push(formattedDate)
          hydraulicOilTempAxisY.value.push(node.hydraulicOilTemp)
          powerBatteryChargeAxisX.value.push(formattedDate)
          powerBatteryChargeAxisY.value.push(node.powerBatteryCharge)
          powerBatteryLifeAxisX.value.push(formattedDate)
          powerBatteryLifeAxisY.value.push(node.powerBatteryLife)
          powerBatteryTemperatureAxisX.value.push(formattedDate)
          powerBatteryTemperatureAxisY.value.push(node.powerBatteryTemperature)
          powerBatteryVoltsAxisX.value.push(formattedDate)
          powerBatteryVoltsAxisY.value.push(node.powerBatteryVolts)
          speedAxisX.value.push(formattedDate)
          speedAxisY.value.push(node.speed)
          transmissionTempAxisX.value.push(formattedDate)
          transmissionTempAxisY.value.push(node.transmissionTemp)
          turboPressureAxisX.value.push(formattedDate)
          turboPressureAxisY.value.push(node.turboPressure)

          const hours = Number(node.hours ?? 0)
          const km = Number(node.totalOdometerDistance ?? 0)
          const hasHours = Number.isFinite(hours) && hours > 0
          const hasKm = Number.isFinite(km) && km > 0
          if (!hasHours && !hasKm) return

          arrEvents.push({
            title: '',
            start: datePart,
            allDay: true,
            className: 'bg-primary text-white',
            extendedProps: { hours, km },
          })
        })

        calendarOptions.value.events = arrEvents
        if (dtS.value) calendarOptions.value.initialDate = dtS.value
        const hasDailyData = hasAnyPositiveDailyData(rows)
        showMsgNoDataFound.value = !hasDailyData
        showCalendar.value = hasDailyData && (axisY.value.some(v => Number(v) > 0) || arrEvents.length > 0)
        isLoadingDaily.value = false
        nextTick(updateCalendar)
      } catch (e) {
        tableData.value = []
        showSnackbar({
          title: $t('Error fetching report'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      } finally {
        isLoadingDaily.value = false
      }
    }

    // Table helpers (Composition API) — usados no template do "Dados diários"
    const selectAllTableColumns = () => {
      selectedTableColumns.value = availableTableColumns.value.map(col => col.key)
      saveTableColumnPreferences()
    }
    const deselectAllTableColumns = () => {
      selectedTableColumns.value = []
      saveTableColumnPreferences()
    }
    const formatTableCellValue = (key, value) => {
      if (value === null || value === undefined || value === 0) return '-'
      if (key === 'date') {
        try {
          const date = new Date(value)
          if (Number.isNaN(date.getTime())) return value

          // Se vier UTC (ex: 2026-04-22T00:00:00Z), formatar em UTC para não “voltar um dia” no fuso local.
          const useUTC = typeof value === 'string' && /Z$/.test(value)
          const day = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(2, '0')
          const month = String((useUTC ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0')
          const year = useUTC ? date.getUTCFullYear() : date.getFullYear()
          return `${day}/${month}/${year}`
        } catch (_) {
          return value
        }
      }
      if (typeof value === 'number') {
        // distância fica mais “humana” sem casas
        if (key === 'totalOdometerDistance') return value.toFixed(0)
        return value.toFixed(2)
      }
      return value
    }
    const getCellClass = (key, value) => {
      const classes = []
      classes.push(key === 'date' ? 'text-center' : 'text-right')
      if (value === null || value === undefined || value === 0) classes.push('text-muted')
      return classes.join(' ')
    }
    const exportTableToExcel = async () => {
      if (isExportingTable.value) return
      isExportingTable.value = true
      try {
        const response = await store.dispatch('app-queries/exportMonthlySummaryToExcel', {
          assetId: myId,
          start: dtS.value,
          end: dtE.value,
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url

        const date = new Date(dtS.value)
        const monthNumber = date.getUTCMonth() + 1
        const year = date.getUTCFullYear()
        link.setAttribute('download', `${$t('Daily Telemetry Data')}_${monthNumber}_${year}.xls`)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        showSnackbar({ title: $t('Export successful'), icon: 'CheckIcon', variant: 'success' })
      } catch (e) {
        showSnackbar({ title: $t('Error exporting report'), icon: 'AlertTriangleIcon', variant: 'danger' })
      } finally {
        isExportingTable.value = false
      }
    }

    const tableColumns = computed(() => ([
      { key: 'date', label: $t('Date'), sortable: true },
      { key: 'hours', label: $t('Working Hours'), sortable: true },
      { key: 'totalOdometerDistance', label: `${$t('Distance')} (km)`, sortable: true },
      { key: 'fuelLevel', label: `${$t('Fuel Level')} (%)`, sortable: true },
      { key: 'meanFuelConsumption', label: `${$t('Mean Fuel Consumption')} (L/h)`, sortable: true },
      { key: 'engineCoolantTemp', label: `${$t('Engine Coolant Temperature')} (°C)`, sortable: true },
      { key: 'hydraulicOilTemp', label: `${$t('Hydraulic Oil Temperature')} (°C)`, sortable: true },
      { key: 'transmissionTemp', label: `${$t('Transmission Oil Temperature')} (°C)`, sortable: true },
      { key: 'dayErrors', label: $t('Day Errors'), sortable: true },
      { key: 'fuelPressure', label: `${$t('Fuel Pressure')} (kPa)`, sortable: true },
      { key: 'engineOilPressure', label: `${$t('Engine Oil Pressure')} (kPa)`, sortable: true },
      { key: 'turboPressure', label: `${$t('Turbo Pressure')} (kPa)`, sortable: true },
      { key: 'batteryVoltage', label: `${$t('Battery Voltage')} (V)`, sortable: true },
      { key: 'acceleration', label: $t('Acceleration'), sortable: true },
      { key: 'speed', label: `${$t('Speed')} (km/h)`, sortable: true },
      { key: 'engineLoad', label: `${$t('Engine Load')} (%)`, sortable: true },
      { key: 'rpm', label: $t('RPM'), sortable: true },
      { key: 'dayErrorsCount', label: $t('Day Errors Count'), sortable: true },
      { key: 'hydraulicPressure', label: `${$t('Hydraulic Oil Pressure')} (kPa)`, sortable: true },
      { key: 'powerBatteryCharge', label: `${$t('Power Battery Charge')} (%)`, sortable: true },
      { key: 'powerBatteryLife', label: `${$t('Power Battery Life')} (%)`, sortable: true },
      { key: 'powerBatteryVolts', label: `${$t('Power Battery Volts')} (V)`, sortable: true },
      { key: 'powerBatteryTemperature', label: `${$t('Power Battery Temperature')} (°C)`, sortable: true },
    ]))

    const visibleTableFields = computed(() => tableColumns.value.filter(c => selectedTableColumns.value.includes(c.key)))
    const availableTableColumns = computed(() => tableColumns.value.map(c => ({ key: c.key, label: c.label || c.key })))

    const hasTableData = computed(() => {
      if (!tableData.value || tableData.value.length === 0) return false
      const numericFields = [
        'hours', 'totalOdometerDistance', 'fuelLevel', 'meanFuelConsumption', 'engineCoolantTemp',
        'hydraulicOilTemp', 'transmissionTemp', 'dayErrors', 'fuelPressure',
        'engineOilPressure', 'turboPressure', 'batteryVoltage', 'acceleration',
        'speed', 'engineLoad', 'rpm', 'dayErrorsCount', 'hydraulicPressure',
        'powerBatteryCharge', 'powerBatteryLife', 'powerBatteryVolts',
        'powerBatteryTemperature',
      ]
      return tableData.value.some(row => numericFields.some(field => Number(row?.[field] ?? 0) > 0))
    })

    const shouldShowChart = (chart) => {
      const y = chart?.axisY
      return Array.isArray(y) && !isLoadingDaily.value && y.some(v => Number(v) > 0)
    }

    const chartsList = computed(() => ([
      { id: 'workingHours', title: $t('Working Hours'), axisX: axisX.value, axisY: axisY.value, unit: $t('Worked Hours') },
      { id: 'fuelLevel', title: $t('Level Fuel'), axisX: fuelAxisX.value, axisY: fuelAxisY.value, unit: '%' },
      { id: 'fuelConsumption', title: $t('Average Fuel Consumption'), axisX: consumptionAxisX.value, axisY: consumptionAxisY.value, unit: 'L/h' },
      { id: 'batteryVoltage', title: $t('Battery Voltage'), axisX: batteryVoltageAxisX.value, axisY: batteryVoltageAxisY.value, unit: 'V' },
      { id: 'dayErrors', title: $t('Day errors'), axisX: dayErrorsAxisX.value, axisY: dayErrorsAxisY.value, unit: '' },
      { id: 'fuelPressure', title: $t('Fuel Pressure'), axisX: fuelPressureAxisX.value, axisY: fuelPressureAxisY.value, unit: 'kPa' },
      { id: 'engineLoad', title: $t('Engine Load'), axisX: engineLoadAxisX.value, axisY: engineLoadAxisY.value, unit: '%' },
      { id: 'engineOilPressure', title: $t('Engine Oil Pressure'), axisX: engineOilPressureAxisX.value, axisY: engineOilPressureAxisY.value, unit: 'kPa' },
      { id: 'engineCoolantTemp', title: $t('Engine Coolant Temperature'), axisX: engineCoolantTempAxisX.value, axisY: engineCoolantTempAxisY.value, unit: 'ºC' },
      { id: 'hydraulicPressure', title: $t('Hydraulic Oil Pressure'), axisX: hydraulicPressureAxisX.value, axisY: hydraulicPressureAxisY.value, unit: 'kPa' },
      { id: 'hydraulicOilTemp', title: $t('Hydraulic Oil Temperature'), axisX: hydraulicOilTempAxisX.value, axisY: hydraulicOilTempAxisY.value, unit: 'ºC' },
      { id: 'powerBatteryCharge', title: $t('Power Battery Charge'), axisX: powerBatteryChargeAxisX.value, axisY: powerBatteryChargeAxisY.value, unit: '%' },
      { id: 'powerBatteryLife', title: $t('Power Battery Life'), axisX: powerBatteryLifeAxisX.value, axisY: powerBatteryLifeAxisY.value, unit: '%' },
      { id: 'powerBatteryTemp', title: $t('Power Battery Temperature'), axisX: powerBatteryTemperatureAxisX.value, axisY: powerBatteryTemperatureAxisY.value, unit: 'ºC' },
      { id: 'powerBatteryVolts', title: $t('Power Battery Volts'), axisX: powerBatteryVoltsAxisX.value, axisY: powerBatteryVoltsAxisY.value, unit: 'V' },
      { id: 'speed', title: $t('Speed'), axisX: speedAxisX.value, axisY: speedAxisY.value, unit: 'Km/h' },
      { id: 'transmissionTemp', title: $t('Transmission Oil Temperature'), axisX: transmissionTempAxisX.value, axisY: transmissionTempAxisY.value, unit: 'ºC' },
      { id: 'turboPressure', title: $t('Turbo Pressure'), axisX: turboPressureAxisX.value, axisY: turboPressureAxisY.value, unit: 'kPa' },
    ].map(c => ({
      ...c,
      expanded: chartsExpanded.value[c.id] !== false,
    }))))

    const visibleChartsCount = computed(() => chartsList.value.filter(c => shouldShowChart(c)).length)
    const expandedChartsCount = computed(() => chartsList.value.filter(c => shouldShowChart(c) && c.expanded).length)
    const hasStatusData = computed(() => (
      Array.isArray(statusValue.value)
      && statusValue.value.some(valor => Number(valor) > 0)
    ))

    const chartIcon = (chartId) => {
      const icons = {
        workingHours: 'ClockIcon',
        fuelLevel: 'DropletIcon',
        fuelConsumption: 'TrendingDownIcon',
        batteryVoltage: 'BatteryIcon',
        dayErrors: 'AlertTriangleIcon',
        fuelPressure: 'ActivityIcon',
        engineLoad: 'CpuIcon',
        engineOilPressure: 'DropletIcon',
        engineCoolantTemp: 'ThermometerIcon',
        hydraulicPressure: 'DropletIcon',
        hydraulicOilTemp: 'ThermometerIcon',
        powerBatteryCharge: 'BatteryChargingIcon',
        powerBatteryLife: 'BatteryIcon',
        powerBatteryTemp: 'ThermometerIcon',
        powerBatteryVolts: 'ZapIcon',
        speed: 'NavigationIcon',
        transmissionTemp: 'ThermometerIcon',
        turboPressure: 'WindIcon',
      }
      return icons[chartId] || 'BarChart2Icon'
    }

    const saveChartsPreferences = () => {
      try { localStorage.setItem('monthlyTelemetryChartsExpanded', JSON.stringify(chartsExpanded.value)) } catch (_) {}
    }
    const loadChartsPreferences = () => {
      try {
        const raw = localStorage.getItem('monthlyTelemetryChartsExpanded')
        if (raw) chartsExpanded.value = JSON.parse(raw) || {}
      } catch (_) {}
    }
    const toggleChart = (chartId) => {
      chartsExpanded.value = {
        ...chartsExpanded.value,
        [chartId]: !(chartsExpanded.value[chartId] !== false),
      }
      saveChartsPreferences()
    }
    const expandAllCharts = () => {
      const next = { ...chartsExpanded.value }
      chartsList.value.forEach(chart => {
        if (shouldShowChart(chart)) next[chart.id] = true
      })
      chartsExpanded.value = next
      saveChartsPreferences()
    }
    const collapseAllCharts = () => {
      const next = { ...chartsExpanded.value }
      chartsList.value.forEach(chart => {
        if (shouldShowChart(chart)) next[chart.id] = false
      })
      chartsExpanded.value = next
      saveChartsPreferences()
    }

    const saveTableColumnPreferences = () => {
      try { localStorage.setItem('monthlyTelemetrySelectedTableColumns', JSON.stringify(selectedTableColumns.value)) } catch (_) {}
    }
    const initializeTableColumnSelection = () => {
      try {
        const raw = localStorage.getItem('monthlyTelemetrySelectedTableColumns')
        if (raw) selectedTableColumns.value = JSON.parse(raw) || []
      } catch (_) {}
      if (!selectedTableColumns.value || selectedTableColumns.value.length === 0) {
        selectedTableColumns.value = ['date', 'hours', 'totalOdometerDistance', 'fuelLevel', 'meanFuelConsumption', 'engineCoolantTemp', 'speed']
      }
    }

    watch(selectedTableColumns, saveTableColumnPreferences, { deep: true })

    // Garantia: sempre que o intervalo mudar, recarregar o heatmap (debounce para dtS/dtE)
    let placesReloadTimer = null
    watch([dtS, dtE], () => {
      if (!dtS.value || !dtE.value) return
      if (placesReloadTimer) clearTimeout(placesReloadTimer)
      placesReloadTimer = setTimeout(() => {
        fetchPlaces()
      }, 0)
    })

    const getHeatMap = () => instance?.proxy?.$refs?.myMap?.mapObject

    const fitHeatMap = () => {
      const map = getHeatMap()
      const points = latlngs.value.map(point => [point[0], point[1]]).filter(point => Number.isFinite(point[0]) && Number.isFinite(point[1]))
      if (!map || points.length === 0) return
      if (points.length === 1) {
        map.setView(points[0], 14)
        return
      }
      map.fitBounds(points, { padding: [36, 36], maxZoom: 16 })
    }

    const refreshHeatMapSize = () => {
      nextTick(() => {
        const map = getHeatMap()
        if (!map) return
        map.invalidateSize()
        if (isHeatMapFullscreen.value) map.scrollWheelZoom.enable()
        else map.scrollWheelZoom.disable()
        fitHeatMap()
      })
    }

    const onHeatMapReady = () => {
      refreshHeatMapSize()
    }

    const toggleHeatMapFullscreen = () => {
      const wasClosed = !showHeatMap.value
      isHeatMapFullscreen.value = !isHeatMapFullscreen.value
      if (isHeatMapFullscreen.value) showHeatMap.value = true
      document.body.style.overflow = isHeatMapFullscreen.value ? 'hidden' : ''
      setTimeout(refreshHeatMapSize, wasClosed ? 320 : 60)
    }

    const onHeatMapKeydown = (event) => {
      if (event.key === 'Escape' && isHeatMapFullscreen.value) toggleHeatMapFullscreen()
    }

    watch(showHeatMap, (open) => {
      if (!open && isHeatMapFullscreen.value) {
        isHeatMapFullscreen.value = false
        document.body.style.overflow = ''
      }
      if (open) refreshHeatMapSize()
    })

    // Register module
    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      document.removeEventListener('keydown', onHeatMapKeydown)
      document.body.style.overflow = ''
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
    })

    const refreshReport = () => {
      fetchDailyWorkHours()
      fetchPlaces()
    }

    onMounted(() => {
      document.addEventListener('keydown', onHeatMapKeydown)
      startDates()
      initializeTableColumnSelection()
      loadChartsPreferences()
      fetchAsset()
      fetchDailyWorkHours()
      if (localStorage.layerHeatMap) {
        chooseLayer(localStorage.layerHeatMap)
      }
      nextTick(() => {
        calendarKey.value += 1
        updateCalendar()
      })
    })

    return {
      QUERIES_APP_STORE_MODULE_NAME,
      deviceId,
      calendarKey,
      tooltipBtn,
      showFilters,
      dateStart,
      dateEnd,
      dateRangePreset,
      singleDateConfig,
      periodPresets,
      activeFiltersCount,
      applyFilters,
      resetFilters,
      setDatePreset,
      onCustomDateChange,
      dtS,
      dtE,
      isLoading,
      isLoadingDaily,
      showMsgNoDataFound,
      showCalendar,
      center,
      latlngs,
      url,
      btnGround,
      btnMap,
      showHeatMap,
      isHeatMapFullscreen,
      heatMapZoom,
      heatMapRenderKey,
      heatMapOptions,
      heatMapGradient,
      toggleHeatMapFullscreen,
      fitHeatMap,
      onHeatMapReady,
      showStatusChart,
      chooseLayer,
      calendarOptions,
      tableData,
      selectedTableColumns,
      isExportingTable,
      showTable,
      tableColumns,
      visibleTableFields,
      availableTableColumns,
      hasTableData,
      chartsList,
      visibleChartsCount,
      expandedChartsCount,
      updateCalendar,
      formatKpiValue,
      toggleChart,
      shouldShowChart,
      expandAllCharts,
      collapseAllCharts,
      exportSummary,
      selectAllTableColumns,
      deselectAllTableColumns,
      exportTableToExcel,
      formatTableCellValue,
      getCellClass,
      hasKpiData,
      kpiCards,
      isRefreshing,
      formatAverageKpiValue,
      startHourmeter,
      endHourmeter,
      workHours,
      averageDayWorkedHours,
      identifier,
      imgUrl,
      modelInfo,
      color,
      loadingAsset,
      assetPlate,
      assetChassis,
      assetManufacturer,
      assetModel,
      assetNickname,
      assetGroupName,
      assetCustomerName,
      assetAvatar,
      periodLabel,
      copyToClipboard,
      goBack,
      handleImageError,
      refreshReport,
      chartIcon,
      hasStatusData,
      axisX,
      axisY,
      myId,
      statusLabels,
      statusValue,
      consumptionAxisX,
      consumptionAxisY,
      fuelAxisX,
      fuelAxisY,
      metricsPercentages,
      metricsLabels,
      batteryVoltageAxisX,
      batteryVoltageAxisY,
      dayErrorsAxisX,
      dayErrorsAxisY,
      fuelPressureAxisX,
      fuelPressureAxisY,
      engineLoadAxisX,
      engineLoadAxisY,
      engineOilPressureAxisX,
      engineOilPressureAxisY,
      engineCoolantTempAxisX,
      engineCoolantTempAxisY,
      hydraulicPressureAxisX,
      hydraulicPressureAxisY,
      hydraulicOilTempAxisX,
      hydraulicOilTempAxisY,
      powerBatteryChargeAxisX,
      powerBatteryChargeAxisY,
      powerBatteryLifeAxisX,
      powerBatteryLifeAxisY,
      powerBatteryTemperatureAxisX,
      powerBatteryTemperatureAxisY,
      powerBatteryVoltsAxisX,
      powerBatteryVoltsAxisY,
      speedAxisX,
      speedAxisY,
      transmissionTempAxisX,
      transmissionTempAxisY,
      turboPressureAxisX,
      turboPressureAxisY,
    }
  }

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/chart-apex.scss';

.dropdown-menu.graphic-telemetry-column-menu {
  min-width: 280px !important;
  max-width: 320px !important;
  border-radius: 0.5rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;

  .graphic-telemetry-column-options {
    height: 240px !important;
    max-height: 240px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: block;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d8d6de;
      border-radius: 4px;
    }

    .custom-checkbox .custom-control-label .column-option-text {
      font-size: 0.875rem;
      line-height: 1.4;
    }
  }
}

.monthly-telemetry-report-wrapper {
  .spin-icon {
    animation: telemetry-spin 1s linear infinite;
  }

  @keyframes telemetry-spin {
    100% {
      transform: rotate(360deg);
    }
  }

  .period-picker {
    min-width: 230px;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);

    .form-control,
    .input-group-text {
      background: transparent;
      cursor: pointer;
    }
  }

  .min-width-0 {
    min-width: 0;
  }

  .empty-copy {
    max-width: 420px;
  }

  .asset-header-card {
    border: 1px solid rgba(115, 103, 240, 0.14) !important;
    border-left: 4px solid #7367f0 !important;
    border-radius: 0.5rem;
    background-color: #fff;

    .asset-header-content {
      min-height: 64px;
      display: flex;
      align-items: center;
    }

    .back-btn {
      border: 1px solid rgba(0, 0, 0, 0.08);
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(115, 103, 240, 0.1) !important;
        color: #7367f0 !important;
        border-color: #7367f0;
      }
    }

    .asset-avatar-box {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      background: linear-gradient(135deg, #f8f8f8 0%, #eceef2 100%);
      border: 1px solid rgba(0, 0, 0, 0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .asset-avatar-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 4px;
      }
    }

    .license-plate-badge {
      display: inline-flex;
      align-items: center;
      background: #fff;
      border: 2px solid #2b2b2b;
      border-radius: 6px;
      padding: 2px 8px;
      font-size: 0.95rem;
      font-weight: 800;
      letter-spacing: 1px;
      color: #2b2b2b;

      .plate-number {
        font-family: Consolas, 'Courier New', monospace;
      }

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
      }
    }

    .asset-brand-text {
      font-size: 1.05rem;
      color: #7367f0 !important;
    }

    .asset-model-title {
      font-size: 1.05rem;
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      background: #f8f8f8;
      border: 1px solid #ebe9f1;
      border-radius: 20px;
      padding: 4px 10px;
      font-size: 0.8rem;

      .meta-label {
        font-weight: 600;
        color: #6e6b7b;
        margin-right: 4px;
      }

      .meta-value {
        font-weight: 600;
        color: #5e5873;
      }
    }
  }

  .kpi-card {
    border-radius: 0.5rem;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }

    .kpi-unit {
      font-size: 0.8rem;
      font-weight: 500;
      color: #b9b9c3;
    }

    .kpi-footer {
      line-height: 1.3;
    }
  }

  .section-card-header,
  .chart-card-header,
  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 0.85rem 1rem;
  }

  .chart-card,
  .section-card {
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .chart-card-header {
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: inherit;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(115, 103, 240, 0.04);
    }

    &:focus {
      outline: none;
      box-shadow: inset 0 0 0 2px rgba(115, 103, 240, 0.35);
    }
  }

  .chart-title {
    font-size: 1rem;
    font-weight: 700;
    color: #5e5873;
  }

  .legend-row {
    gap: 8px;
  }

  .legend-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 20px;
    padding: 0.25rem 0.7rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #6e6b7b;
  }

  .wc-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 16px;
    padding: 0 4px;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 800;
    line-height: 1;
  }

  .wc-tag-hours {
    background: rgba(115, 103, 240, 0.14);
    color: #7367f0;
  }

  .wc-tag-km {
    background: rgba(40, 199, 111, 0.16);
    color: #28c76f;
  }

  .app-calendar {
    padding: 0 0.75rem 0.75rem;

    .fc-toolbar-title {
      font-size: 1.05rem;
      font-weight: 700;
    }

    .fc-daygrid-event {
      border: 0;
      border-radius: 6px;
      margin-top: 2px;
    }

    .fc-daygrid-day-number {
      font-weight: 600;
      font-size: 0.8rem;
    }
  }

  .work-cal-event {
    display: flex;
    align-items: stretch;
    gap: 6px;
    padding: 3px 6px;
    line-height: 1.1;

    .wc-dot {
      width: 3px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.85);
    }

    .wc-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .wc-line {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .wc-value {
      font-weight: 700;
      font-size: 0.78rem;
    }

    .wc-tag-hours,
    .wc-tag-km {
      background: rgba(255, 255, 255, 0.22);
      color: #fff;
    }
  }

  .heat-map-title-btn {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 0;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
  }

  .heat-map-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
  }

  .heat-map-meta {
    gap: 8px;
  }

  .heat-map {
    position: relative;
    width: 100%;
    height: 480px;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 1px solid #ebe9f1;
  }

  .heat-map-canvas {
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  .heat-legend {
    position: absolute;
    left: 12px;
    bottom: 12px;
    z-index: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 2px 10px rgba(34, 41, 47, 0.12);
    font-size: 0.72rem;
    font-weight: 700;
    color: #5e5873;
    pointer-events: none;
  }

  .heat-legend-bar {
    width: 92px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(90deg, #3b82f6, #22d3ee, #22c55e, #facc15, #ef4444);
  }

  .heat-map-card.is-fullscreen {
    position: fixed !important;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    width: 100vw;
    height: 100vh;
    margin: 0 !important;
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
    background: #fff;

    .collapse.show {
      display: flex !important;
      flex: 1;
      flex-direction: column;
      min-height: 0;
    }

    .heat-map-stage {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
    }

    .heat-map {
      flex: 1;
      height: auto;
      min-height: 0;
      border-radius: 0.5rem;
    }
  }

  .map-layer-switch .btn {
    font-weight: 600;
  }

  .table-actions {
    gap: 8px;
  }

  .table-container {
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .excel-table {
    margin-bottom: 0;
    font-size: 0.84rem;

    .excel-header th {
      font-weight: 700;
      text-align: center;
      vertical-align: middle;
      padding: 0.7rem 0.5rem;
      color: #5e5873;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      background: #f8f8f8;
      border-bottom: 1px solid #ebe9f1;
      white-space: nowrap;
    }

    .excel-row td {
      padding: 0.45rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #f1f0f5;
    }

    tbody tr:hover {
      background: rgba(115, 103, 240, 0.04);
    }
  }

  .excel-cell {
    font-size: 0.8rem;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.text-right {
      text-align: right;
    }

    &.text-center {
      text-align: center;
    }
  }

  @media (max-width: 767.98px) {
    .period-picker {
      width: 100%;
      min-width: 0;
    }

    .report-toolbar {
      width: 100%;
    }

    .chart-title {
      font-size: 0.92rem;
    }

    .heat-map {
      height: 340px;
    }
  }
}

body.dark-layout {
  .monthly-telemetry-report-wrapper {
    .period-picker {
      background: #283046;
    }

    .asset-header-card {
      background-color: #283046;
      border-color: rgba(255, 255, 255, 0.08) !important;
      border-left-color: #7367f0 !important;

      .back-btn {
        border-color: rgba(255, 255, 255, 0.12);
        color: #b4b7bd;
      }

      .asset-avatar-box {
        background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
        border-color: rgba(255, 255, 255, 0.08);
      }

      .license-plate-badge {
        background: #161d31;
        border-color: #676d7d;
        color: #d0d2d6;
      }

      .asset-model-title {
        color: #d0d2d6 !important;
      }

      .meta-chip {
        background: #161d31;
        border-color: rgba(255, 255, 255, 0.08);

        .meta-label {
          color: #b4b7bd;
        }

        .meta-value {
          color: #d0d2d6;
        }
      }
    }

    .chart-title {
      color: #d0d2d6;
    }

    .chart-card-header:hover {
      background: rgba(115, 103, 240, 0.08);
    }

    .legend-pill {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.08);
      color: #b4b7bd;
    }

    .heat-map,
    .table-container {
      border-color: rgba(255, 255, 255, 0.08);
    }

    .heat-legend {
      background: rgba(22, 29, 49, 0.92);
      color: #d0d2d6;
    }

    .heat-map-card.is-fullscreen {
      background: #283046;
    }

    .excel-table {
      .excel-header th {
        background: #1e2433;
        color: #d0d2d6;
        border-bottom-color: rgba(255, 255, 255, 0.08);
      }

      .excel-row td {
        border-top-color: rgba(255, 255, 255, 0.06);
      }
    }
  }

  .dropdown-menu.graphic-telemetry-column-menu {
    .graphic-telemetry-column-options::-webkit-scrollbar-thumb {
      background: #3b4253;
    }
  }
}
</style>
